import { Container, ActionIcon, Menu, Button, createPolymorphicComponent } from '@mantine/core';
import type { BoxProps } from '@mantine/core';
import { Link, useParams } from 'react-router';
import { useState } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { styled, useTheme } from '@epihub/web/src/_ctx/theme';
import { assets } from '../assets';
import { useSfyr } from '../_ctx/sfyr';
import { LanguageToggle } from './LanguageToggle';

const StickyHeader = createPolymorphicComponent<'header', BoxProps>(styled.header`
  backdrop-filter: blur(12px);
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 12px 0;
`);

const MobileHeader = styled.header`
  backdrop-filter: blur(12px);
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 12px 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 16px;
`;

const HeaderNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
  @media (min-width: 769px) {
    gap: 24px;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none !important;
  font-size: 14px;
  font-weight: 500;
  transition: opacity 0.2s;
  white-space: nowrap;
  color: inherit;
  &:visited, &:active {
    color: inherit;
  }
  &:hover {
    opacity: 0.7;
  }
`;

export const Header = () => {
  const [menuOpened, setMenuOpened] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const theme = useTheme();
  const sfyr = useSfyr();
  const { lang } = useParams<{ lang: string }>();
  const langPrefix = lang ? `/${lang}` : '';

  const navLinks = [
    {
      label: sfyr('landing.{lang}.nav.home', { defaults: { 'landing.{lang=en}.nav.home': 'Home' } }),
      href: `${langPrefix}/`,
    },
    {
      label: sfyr('landing.{lang}.nav.about', { defaults: { 'landing.{lang=en}.nav.about': 'About' } }),
      href: `${langPrefix}/#about`,
    },
    {
      label: sfyr('landing.{lang}.nav.blog', { defaults: { 'landing.{lang=en}.nav.blog': 'Blog' } }),
      href: `${langPrefix}/blog`,
    },
  ];

  const quoteLabel = sfyr('landing.{lang}.nav.quote', { defaults: { 'landing.{lang=en}.nav.quote': 'Quote' } });

  if (isMobile) {
    return (
      <MobileHeader
        style={{
          borderBottom: `1px solid ${theme.v0.col.backgroundSecondary.hex}`,
          backgroundColor: theme.v0.col.background.rgba(0.85),
        }}
      >
        <Container size='lg'>
          <HeaderRow>
            <Link to={`${langPrefix}/`} style={{ display: 'flex', alignItems: 'center' }}>
              <assets.LogoRect.Component height={28} />
            </Link>
            <HeaderNav>
              <LanguageToggle />
              <a href='mailto:info@epilogo.co?subject=Quote%20request'>
                <Button size='xs' variant='light' color={theme.v0.col.primary.hex}>
                  {quoteLabel}
                </Button>
              </a>
              <Menu opened={menuOpened} onOpen={() => setMenuOpened(true)} onClose={() => setMenuOpened(false)}>
                <Menu.Target>
                  <ActionIcon variant='subtle' size='lg'>
                    ☰
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {navLinks.map((link) => (
                    <Menu.Item key={link.href}>
                      <Link to={link.href} style={{ color: 'inherit' }}>
                        {link.label}
                      </Link>
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
            </HeaderNav>
          </HeaderRow>
        </Container>
      </MobileHeader>
    );
  }

  return (
    <StickyHeader
      style={{
        borderBottom: `1px solid ${theme.v0.col.backgroundSecondary.hex}`,
        backgroundColor: theme.v0.col.background.rgba(0.85),
      }}
    >
      <Container size='lg'>
        <HeaderRow>
          <Link to={`${langPrefix}/`} style={{ display: 'flex', alignItems: 'center' }}>
            <assets.LogoRect.Component height={28} />
          </Link>
          <HeaderNav>
            {navLinks.map((link) => (
              <NavLink
                key={link.href}
                to={link.href}
                style={{ color: theme.v0.col.text.hex }}
              >
                {link.label}
              </NavLink>
            ))}
            <LanguageToggle />
            <a href='mailto:info@epilogo.co?subject=Quote%20request'>
              <Button size='sm' variant='light' color={theme.v0.col.primary.hex}>
                {quoteLabel}
              </Button>
            </a>
          </HeaderNav>
        </HeaderRow>
      </Container>
    </StickyHeader>
  );
};
